import * as React from 'react';
import {
  createContext,
  Dispatch,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { getData } from 'stores/session-storage';
import { getActions, RegParticipantsActions } from './actions';
import {
  registrationParticipantsReducer,
  RegParticipantAction,
} from './reducer';
import { ActionType, LoadingType, RegParticipantState } from './types';

export const defaultRegParticipantsState: RegParticipantState = {
  loading: {
    [LoadingType.GetFormResults]: false,
    [LoadingType.GetColumns]: false,
    [LoadingType.GetUniqueRegistrants]: false,
    [LoadingType.GetFilters]: false,
  },
  exportModalTitle: 'Generate Export',
  filters: [],
  activeFilters: { status: ['submitted'] },
  searchTerm: '',
  selectedColumns: [],
  formResults: null,
  columns: [],
};

const RegParticipantsContext = createContext<RegParticipantState | undefined>(
  defaultRegParticipantsState
);
const DispatchContext = createContext<
  Dispatch<RegParticipantAction> | undefined
>(undefined);

export const RegistrationParticipantsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(
    registrationParticipantsReducer,
    defaultRegParticipantsState
  );

  // Fetch selected columns, filters, and search term on load from session storage
  useEffect(() => {
    const storedFilters = getData().registration;
    const { filter } = storedFilters || {};
    let activeFilters = { status: ['submitted'] };
    // Fetch cached filters and search term
    if (filter) {
      const { searchTerm, ...filters } = filter;
      if (filters && Object.keys(filters).length) {
        activeFilters = filters;
      }

      dispatch({
        type: ActionType.SetSearchTerm,
        payload: { searchTerm: searchTerm || '' },
      });
    }
    dispatch({
      type: ActionType.SetActiveFilters,
      payload: { activeFilters },
    });
  }, []);

  return (
    <RegParticipantsContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        {children}
      </DispatchContext.Provider>
    </RegParticipantsContext.Provider>
  );
};

export const useRegistrationParticipants = (): RegParticipantState => {
  const context = useContext(RegParticipantsContext);
  if (context === undefined) {
    throw new Error(
      'useRegistrationParticipants must be used within a RegistrationParticipantsProvider'
    );
  }
  return context;
};

export const useRegistrationParticipantsActions =
  (): RegParticipantsActions => {
    const state = useContext(RegParticipantsContext);
    const dispatch = useContext(DispatchContext);

    if (dispatch === undefined) {
      throw new Error(
        'useRegistrationParticipantsActions must be used within a RegistrationParticipantsProvider'
      );
    }
    return getActions(state, dispatch);
  };
